import request, { AxiosResponse } from "axios";
import {
  SalesforceResyncPreview,
  SalesforceResyncView,
} from "./persistSalesforceResync";
import { base } from "./persistSalesforceSync";

/**
 * The Sync criteria routes of the Salesforce v2 sync: the rules that choose
 * which Leads and Contacts come in, read from and saved to the server, never
 * from the browser's copy of the account.
 *
 * The types mirror what the server answers. The server's side of this is
 * packages/api/src/controllers/salesforce_sync_controller.ts (GET and POST
 * /settings/criteria) with the shapes in
 * services/salesforce/v2/criteria/criteriaView.ts, saveCriteria.ts,
 * assertSaveable.ts and criteriaMessages.ts. Nothing here calls Salesforce.
 *
 * Saving needs a signed-in team member and an account on v2. The generic
 * service route (`accountStore.saveServiceConfig`) answers 400
 * `salesforce_v2_old_criteria_route` for `syncCriteria` on v2, so every save of
 * these rules goes through `postSalesforceCriteria`.
 */

export type SalesforceCriteriaObject = "Lead" | "Contact";

/**
 * A side of the stored rules: Lead and Contact choose which people come in;
 * Opportunity (`{ off?, windowMonths?, rules? }`) chooses which Opportunities
 * Encharge keeps up to date; Campaign (`{ off?, windowMonths?, includeActive?,
 * includeRecent?, picks?, rules? }`) chooses which Campaigns Encharge copies, and
 * is OFF until the customer chooses. Neither is a people object, so the count of
 * people (below) never names them.
 */
export type SalesforceCriteriaSide =
  | SalesforceCriteriaObject
  | "Opportunity"
  | "Campaign";

/** Who changed the rules last: the name only, never an email (7.7 of the criteria design). */
export interface SalesforceCriteriaChange {
  /** ISO */
  at: string;
  by: { name: string } | null;
  /** The people objects (Lead, Contact) whose rules changed who matches */
  objects: SalesforceCriteriaObject[];
  /** The Opportunity switch, window or rules changed too */
  opportunity?: true;
  /** The Campaign switch, window, boxes, picks or rules changed too */
  campaign?: true;
  /** The change put earlier rules back (Restore previous rules) */
  restored: boolean;
}

/** GET /settings/criteria */
export interface SalesforceCriteriaView {
  /**
   * The stored `syncCriteria` exactly as it is (`{}` when none is stored): each
   * side `{ filterProperties?, rules? }`. A side the customer does not change is
   * sent back as it was loaded.
   */
  criteria: unknown;
  /** The fingerprint of `criteria`; sent back as `basedOn` when the rules are saved */
  key: string;
  /**
   * The sync has started. False while a new connection waits for Start syncing,
   * when saving needs no review. Missing from an answer that does not say: the
   * review then appears.
   */
  started?: boolean;
  /** The last change, while the rules stored are the ones it saved; else null */
  lastChange: SalesforceCriteriaChange | null;
  /**
   * With `previous: true` only: the rules before that change, exactly as stored
   * (`{}` for "every Lead and Contact"), or null when there is no such change
   */
  previous?: unknown;
}

export interface SaveSalesforceCriteriaRequest {
  /** The whole `syncCriteria` value: `{ Lead, Contact }` and the Opportunity side when the account has one, each side as the screen wrote it */
  criteria: unknown;
  /** The `key` of the answer the screen started from */
  basedOn: string;
  /** The save puts earlier rules back (Restore previous rules) */
  restore?: boolean;
}

/** POST /settings/criteria */
export interface SaveSalesforceCriteriaAnswer {
  saved: true;
  /** Whether the save can change which Leads, Contacts or Opportunities match (rules moved around do not) */
  changed: boolean;
  /**
   * The read of Opportunities the save owed, when it owed one: `requested` (the
   * sync does it, quietly), or `failed` (the rules are saved, but the sync could
   * not be asked to read the extra Opportunities). Missing when it owed none.
   */
  opportunityPass?: "requested" | "failed";
  /** The same for the read of Campaigns the save owed (CMP1) */
  campaignPass?: "requested" | "failed";
  /** The fingerprint of the rules now stored */
  key: string;
}

/** One thing the server found wrong in the rules it was sent (400). `object` is null for the whole value. */
export interface SalesforceCriteriaProblem {
  object: SalesforceCriteriaSide | null;
  /** From the side: `filterProperties[2].condition`, `rules.include.conditions[1].conditions[0].propertyName` */
  path: string;
  /** A short code: the keys of 6.7 where one fits, `badShape`, `tooBig`, `emptyGroup` */
  problem: string;
}

/** The codes the criteria routes answer with (criteriaMessages.ts). */
export const CRITERIA_CONFLICT_CODE = "salesforce_sync_criteria_conflict";
export const CRITERIA_INVALID_CODE = "salesforce_sync_criteria_invalid";
export const OLD_CRITERIA_ROUTE_CODE = "salesforce_v2_old_criteria_route";
export const CRITERIA_NEEDS_V2_CODE = "salesforce_sync_criteria_needs_v2";
export const NOT_V2_CODE = "salesforce_v2_not_enabled";

/**
 * An error from the Sync criteria routes, with what the server said about it.
 * `status` is missing when there was no answer at all (the network failed).
 */
export type SalesforceCriteriaRequestError = Error & {
  status?: number;
  errorCode?: string;
  /** 400 `salesforce_sync_criteria_invalid`: every problem, with its path */
  problems?: SalesforceCriteriaProblem[];
  /** 409 `salesforce_sync_criteria_conflict`: the name of who saved the rules that are stored now, else null */
  changedBy?: string | null;
  /** ... and when (ISO), else null */
  changedAt?: string | null;
};

export const CRITERIA_CONFLICT_STATUS = 409;

const asCriteriaError = (
  e: any,
  fallback: string
): SalesforceCriteriaRequestError => {
  const body = e?.response?.data?.error;
  const error: SalesforceCriteriaRequestError = new Error(
    body?.message || e?.message || fallback
  );
  error.status = e?.response?.status;
  if (typeof body?.errorCode === "string") error.errorCode = body.errorCode;
  if (Array.isArray(body?.data?.problems)) {
    error.problems = body.data.problems;
  }
  if (body?.errorCode === CRITERIA_CONFLICT_CODE) {
    const { changedBy, changedAt } = body?.data || {};
    error.changedBy =
      typeof changedBy === "string" && changedBy !== "" ? changedBy : null;
    error.changedAt =
      typeof changedAt === "string" && changedAt !== "" ? changedAt : null;
  }
  return error;
};

const callCriteria = async <T>(
  config: { url: string; method: "GET" | "POST"; data?: unknown },
  fallback: string
): Promise<T> => {
  try {
    const res: AxiosResponse<T> = await request({
      ...config,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    throw asCriteriaError(e, fallback);
  }
};

/**
 * Reads the stored rules, their fingerprint, whether the sync has started and
 * the last change. `previous: true` adds the rules before that change, for
 * Restore previous rules.
 */
export const getSalesforceCriteria = ({
  previous = false,
}: { previous?: boolean } = {}) =>
  callCriteria<SalesforceCriteriaView>(
    {
      url: `${base}/settings/criteria${previous ? "?previous=1" : ""}`,
      method: "GET",
    },
    "We could not load your sync criteria."
  );

/**
 * Saves the rules. A 409 `salesforce_sync_criteria_conflict` means the rules
 * stored are not the ones `basedOn` names (the error carries `changedBy` and
 * `changedAt`); a 400 `salesforce_sync_criteria_invalid` carries `problems`.
 */
export const postSalesforceCriteria = (body: SaveSalesforceCriteriaRequest) =>
  callCriteria<SaveSalesforceCriteriaAnswer>(
    {
      url: `${base}/settings/criteria`,
      method: "POST",
      data: {
        criteria: body.criteria,
        basedOn: body.basedOn,
        ...(body.restore ? { restore: true } : {}),
      },
    },
    "We could not save your sync criteria."
  );

// ---------------------------------------------------------------------------
// Check a record (6.9 of the criteria design)

/** What the rules on the screen say about one record. */
export type SalesforceCriteriaCheckOutcome =
  | "comes_in"
  | "kept_out"
  | "not_true"
  | "no_group"
  | "no_email"
  | "converted"
  // an Opportunity: closed before the window starts, and the switch is off
  | "outside_window"
  | "off";

export interface SalesforceCriteriaCheckRecord {
  id: string;
  name: string;
  outcome: SalesforceCriteriaCheckOutcome;
  /** The line to show: "Anna Berg comes in through group 1." */
  message: string;
}

export interface SalesforceCriteriaCheckRequest {
  object: SalesforceCriteriaSide;
  /** An email, or a Salesforce Id of 15 or 18 characters; for an Opportunity its exact name or its Id */
  find: string;
  /** That object's rules as the screen has them, in the stored shape (`{ filterProperties?, rules? }`; for an Opportunity `{ off?, windowMonths?, rules? }`) */
  criteria: unknown;
}

/** POST /criteria/check. The server writes every sentence; `message` is the one for no record or a failure. */
export interface SalesforceCriteriaCheckAnswer {
  result: "checked" | "not_found" | "field_refused" | "failed";
  message: string;
  records: SalesforceCriteriaCheckRecord[];
  /** The field Salesforce does not let Encharge read, for `field_refused` */
  field?: string;
  apiCalls?: number;
}

/** 429: more than 30 checks in 10 minutes for the account. */
export const CRITERIA_CHECK_TOO_MANY_CODE =
  "salesforce_sync_criteria_check_too_many";

/**
 * Checks one record against the rules on the screen, saved or not: one
 * Salesforce call, nothing written. Needs a signed-in team member on a v2
 * account. A 400 `salesforce_sync_criteria_invalid` means the rules cannot be
 * judged (the same checks as a save), a 429 that too many checks were made.
 */
export const postSalesforceCriteriaCheck = (
  body: SalesforceCriteriaCheckRequest
) =>
  callCriteria<SalesforceCriteriaCheckAnswer>(
    {
      url: `${base}/criteria/check`,
      method: "POST",
      data: { object: body.object, find: body.find, criteria: body.criteria },
    },
    "We could not check this record. Try again."
  );

// ---------------------------------------------------------------------------
// The count of a change (7.4 of the criteria design)

/**
 * The review that opens when rules are saved on a sync that is running asks for
 * a count: how many Leads and Contacts start and stop matching, read from
 * Salesforce by the sync's own workers. The server's side is
 * services/salesforce/v2/criteria/CriteriaCountService.ts, criteriaCountView.ts
 * and criteriaCountMessages.ts, and the routes are in
 * controllers/salesforce_sync_controller.ts. Starting and cancelling need a
 * signed-in team member on a v2 account, as a save does. Nothing here writes to
 * Encharge's people or to Salesforce.
 */

/**
 *  counting   the count is being read
 *  done       every object is read; the numbers are final
 *  failed     stopped by something it could not get past; `error` says what
 *  cancelled  stopped by the person (Keep editing, a save without the numbers)
 */
export type SalesforceCriteriaCountState =
  | "counting"
  | "done"
  | "failed"
  | "cancelled";

/** POST /criteria/count takes what a save takes. */
export interface SalesforceCriteriaCountRequest {
  /** The whole `syncCriteria` value the screen would save */
  criteria: unknown;
  /** The `key` of the answer the screen started from */
  basedOn: string;
}

/**
 * What POST /criteria/count answers. `started` and `ready` (a finished count for
 * the same rules, reused) name a count to ask about; the rest say why there is
 * no count, in `message`, which is then the whole line the review shows.
 */
export interface SalesforceCriteriaCountStartAnswer {
  result:
    | "started"
    | "ready"
    | "paused"
    | "needs_reconnect"
    | "not_available"
    | "too_many";
  countId?: string;
  message: string;
}

/** Why a count stopped without numbers. */
export type SalesforceCriteriaCountFailure =
  | "paused"
  | "reconnect"
  | "off"
  | "too_many"
  | "field"
  | "failed"
  | "own"
  | "slow";

/** The four numbers of one object whose saved rules could be judged. */
export interface SalesforceCriteriaCountNumbers {
  /** Records that can be a person: not deleted, with an email, and for Leads not converted */
  checked: number;
  /** Records that pass the saved rules */
  matchNow: number;
  /** Records that fail the saved rules and pass the new ones */
  start: number;
  /** Records that pass the saved rules and fail the new ones */
  stop: number;
}

export interface SalesforceCriteriaCountObject {
  object: SalesforceCriteriaObject;
  /** Only once the count is done, and only for an object whose saved rules were judged */
  numbers?: SalesforceCriteriaCountNumbers;
  /** The object's saved rules could not be judged (7.4.5): why, and what the new rules match */
  notJudged?: {
    kind: "field" | "unreadable";
    /** For `field`: the label of the field Salesforce does not let Encharge read */
    field?: string;
    /** How many records the new rules match */
    matchNew: number;
    message: string;
  };
}

/** A big change (7.6): the server decides it, so the rule lives in one place. */
export interface SalesforceCriteriaCountBig {
  object: SalesforceCriteriaObject;
  /** none: the new rules match no record while some match now */
  kind: "stop" | "start" | "none";
  count: number;
  /** Records that match now */
  of: number;
  message: string;
  /** The label of the Save button that names the number */
  button: string;
}

/** GET /criteria/count/:countId */
export interface SalesforceCriteriaCountView {
  countId: string;
  state: SalesforceCriteriaCountState;
  startedAt: string;
  finishedAt?: string;
  /** The last time a worker moved the count on */
  touchedAt: string;
  /** A count that should be moving has not been touched for a long time */
  stalled: boolean;
  /** Cancel was asked for; it stops at the next step */
  cancelling: boolean;
  progress: SalesforceResyncView["progress"];
  /** In one sentence: what the count is doing, or what it found */
  message: string;
  /** What the counts leave out (7.4.1) */
  caption: string;
  waiting?: SalesforceResyncView["waiting"];
  error?: {
    reason: SalesforceCriteriaCountFailure;
    reasonMessage: string;
    /** The whole line: "We could not count. ... You can still save. ..." */
    message: string;
    object?: SalesforceCriteriaObject;
    field?: string;
    until?: string;
  };
  /** One entry for each object the count reads */
  objects: SalesforceCriteriaCountObject[];
  /** The big changes; empty before the count is done */
  big: SalesforceCriteriaCountBig[];
  /** The live flows that may start; null before the first scan */
  flows: SalesforceResyncPreview["flowsThatMayStart"] | null;
  /** Someone saved other rules since the count began: its numbers are about rules that are not the saved ones */
  outOfDate?: "criteria_changed";
}

/** What GET answers for a count another count took the place of. */
export interface SalesforceCriteriaCountReplaced {
  countId: string;
  replaced: true;
  message: string;
}

export type SalesforceCriteriaCountAnswer =
  | SalesforceCriteriaCountView
  | SalesforceCriteriaCountReplaced;

/** Whether the answer is the one for a count that another count replaced. */
export const isReplacedCount = (
  answer: SalesforceCriteriaCountAnswer | undefined
): answer is SalesforceCriteriaCountReplaced =>
  answer !== undefined && "replaced" in answer && answer.replaced === true;

/** What POST /criteria/count/:countId/cancel answers. */
export interface SalesforceCriteriaCountCancelAnswer {
  result: "cancelled" | "cancelling" | "already_finished" | "try_again";
  message: string;
}

/**
 * Starts the count of a change (or finds a finished one for the same rules). A
 * 409 `salesforce_sync_criteria_conflict` and a 400
 * `salesforce_sync_criteria_invalid` come as they do for a save.
 */
export const postSalesforceCriteriaCount = (
  body: SalesforceCriteriaCountRequest
) =>
  callCriteria<SalesforceCriteriaCountStartAnswer>(
    {
      url: `${base}/criteria/count`,
      method: "POST",
      data: { criteria: body.criteria, basedOn: body.basedOn },
    },
    "We could not count."
  );

/** A count's progress while it reads, then its numbers. */
export const getSalesforceCriteriaCount = (countId: string) =>
  callCriteria<SalesforceCriteriaCountAnswer>(
    {
      url: `${base}/criteria/count/${encodeURIComponent(countId)}`,
      method: "GET",
    },
    "We could not get the count's progress."
  );

/** Stops a count at its next step; its Bulk job in Salesforce is deleted. */
export const cancelSalesforceCriteriaCount = (countId: string) =>
  callCriteria<SalesforceCriteriaCountCancelAnswer>(
    {
      url: `${base}/criteria/count/${encodeURIComponent(countId)}/cancel`,
      method: "POST",
    },
    "We could not stop the count."
  );

// ---------------------------------------------------------------------------
// The count of an Opportunity change

/**
 * What the review of a change of the Opportunity window or rules shows. It is
 * not the count of people above: Opportunities are not read in a Bulk job, and
 * nothing starts or stops matching in the people's sense, because an Opportunity
 * that stops matching is kept and no longer updated, never removed. The server
 * asks Salesforce one question (`SELECT COUNT()`), so the answer is quick. The
 * server's side is services/salesforce/v2/criteria/OpportunityChangeCount.ts.
 */
export type SalesforceOpportunityCountResult =
  | "counted"
  | "nothing_to_read"
  | "paused"
  | "needs_reconnect"
  | "not_available"
  | "field_refused"
  | "failed";

/** What a counted change reads. */
export interface SalesforceOpportunityRead {
  /** `band`: only the closed Opportunities a longer window adds; `window`: the open ones and the ones closed within the whole window, again */
  kind: "band" | "window";
  /** The Opportunities Salesforce holds there, before the rules pick among them */
  count: number;
  /** About how many calls to Salesforce the read takes */
  calls: number;
  /** band: the first day it reads; window: the first day of the window */
  from: string;
  /** band: the day the old window starts, which it stops before */
  to?: string;
  /** window: why the whole window is read */
  because?: "switched_on" | "rules";
}

export interface SalesforceOpportunityCountAnswer {
  result: SalesforceOpportunityCountResult;
  /** The line to show for every result but `counted` */
  message: string;
  change: {
    windowFrom: number;
    windowTo: number;
    widened: boolean;
    shortened: boolean;
    switchedOn: boolean;
    switchedOff: boolean;
    rules: "same" | "tighter" | "other";
  };
  read?: SalesforceOpportunityRead;
  apiCalls: number;
}

/**
 * Counts what a change of the Opportunity side would read, before it is saved. A
 * 409 `salesforce_sync_criteria_conflict` and a 400
 * `salesforce_sync_criteria_invalid` come as they do for a save; a 429 means too
 * many calls to Salesforce in a short time (the limit Check a record has).
 */
export const postSalesforceOpportunityCount = (
  body: SalesforceCriteriaCountRequest
) =>
  callCriteria<SalesforceOpportunityCountAnswer>(
    {
      url: `${base}/criteria/opportunity-count`,
      method: "POST",
      data: { criteria: body.criteria, basedOn: body.basedOn },
    },
    "We could not count the Opportunities."
  );

// ---------------------------------------------------------------------------
// The Campaign picker and the estimate of a Campaign choice (CMP1)

/**
 * What the Campaign screens show before anything is saved: the Campaigns of the
 * customer's Salesforce with the Leads and Contacts each holds (the picker), and
 * what a choice would read and cost (the review). Both are one query to
 * Salesforce, answered at once, and neither writes anything. The server's side is
 * services/salesforce/v2/criteria/CampaignEstimate.ts.
 */

/** Why there is no list or no estimate, besides a list or an estimate. */
export type SalesforceCampaignUnavailable =
  | "no_campaigns"
  | "no_access"
  | "paused"
  | "needs_reconnect"
  | "not_available"
  | "failed";

export interface SalesforceCampaignPickerItem {
  /** The 18 character Salesforce id */
  id: string;
  name: string;
  isActive: boolean;
  type: string | null;
  status: string | null;
  /** YYYY-MM-DD */
  startDate: string | null;
  endDate: string | null;
  leads: number;
  contacts: number;
  /** leads + contacts: members at most */
  members: number;
}

/** GET /settings/campaign-picker */
export interface SalesforceCampaignPickerAnswer {
  result: "listed" | SalesforceCampaignUnavailable;
  /** The line to show for every result but `listed` */
  message: string;
  items: SalesforceCampaignPickerItem[];
  /** The org may have more Campaigns than the 2,000 listed */
  truncated: boolean;
  apiCalls: number;
}

/** What a set of Campaigns comes to. `members` are an upper bound. */
export interface SalesforceCampaignFigures {
  campaigns: number;
  /** ... of which picked by hand */
  picked: number;
  members: number;
}

export interface SalesforceCampaignRemovalFigures {
  callsPerCheck: number;
  /** Time between two checks: an hour unless the 5 percent cap slows it */
  intervalMs: number;
  hourly: boolean;
  checksPerDay: number;
  callsPerDay: number;
  /** The most calls a day the checks may make (5 percent of the allowance) */
  capCallsPerDay: number;
  /** callsPerDay as a share of the allowance, 0 to 1 */
  callsPerDayShare: number;
  /** Vince's note, with the real wait when the cap slows the check */
  note: string;
}

export interface SalesforceCampaignCosts {
  /** The day's allowance of Salesforce calls, and whether Salesforce said it */
  dailyCalls: number;
  dailyCallsKnown: boolean;
  /** Calls the read makes, and as a share of the day (0 to 1) */
  readCalls: number;
  readShare: number;
  usedToday?: number;
  afterShare?: number;
  /** The read would take the day past 80 percent: Save does not let it through */
  refused: boolean;
  removal: SalesforceCampaignRemovalFigures;
}

export type SalesforceCampaignPass = "widen" | "choice" | "rules";

/** POST /criteria/campaign-estimate */
export interface SalesforceCampaignEstimateAnswer {
  result: "counted" | "nothing_to_read" | SalesforceCampaignUnavailable;
  /** The line to show for every result but `counted` */
  message: string;
  change: {
    switchedOn: boolean;
    switchedOff: boolean;
    windowFrom: number;
    windowTo: number;
    widened: boolean;
    shortened: boolean;
    rules: "same" | "tighter" | "other";
    picksAdded: number;
    readsMore: boolean;
    pass?: SalesforceCampaignPass;
  };
  listed?: { campaigns: number; truncated: boolean };
  /** Picks the list no longer holds (kept in the choice) */
  picksNotListed?: number;
  /** The whole choice as it stands */
  chosen?: SalesforceCampaignFigures;
  /** What the save makes Encharge read; zero when it reads nothing */
  read?: SalesforceCampaignFigures;
  /** Campaigns in the choice that the saved choice did not take */
  added?: SalesforceCampaignFigures;
  costs?: SalesforceCampaignCosts;
  apiCalls: number;
}

/** The Campaigns of the customer's Salesforce, for the picker: one query, nothing written. A 429 means too many calls in a short time. */
export const getSalesforceCampaignPicker = () =>
  callCriteria<SalesforceCampaignPickerAnswer>(
    {
      url: `${base}/settings/campaign-picker`,
      method: "GET",
    },
    "We could not list the Campaigns."
  );

/**
 * What a Campaign choice would read and cost, before it is saved. A 409
 * `salesforce_sync_criteria_conflict` and a 400 `salesforce_sync_criteria_invalid`
 * come as they do for a save; a 429 means too many calls to Salesforce in a short
 * time.
 */
export const postSalesforceCampaignEstimate = (
  body: SalesforceCriteriaCountRequest
) =>
  callCriteria<SalesforceCampaignEstimateAnswer>(
    {
      url: `${base}/criteria/campaign-estimate`,
      method: "POST",
      data: { criteria: body.criteria, basedOn: body.basedOn },
    },
    "We could not work out what the Campaigns would cost."
  );
